import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * SKU + MPN for the selected variant (blocks/cst-product-codes.liquid).
 * Listens for the same `productSelect` event as Horizon's product-sku.js and looks the new
 * variant up in the embedded JSON map, because the event's variant data has no metafields.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} skuLine
 * @property {HTMLElement} sku
 * @property {HTMLElement} mpnLine
 * @property {HTMLElement} mpn
 * @property {HTMLScriptElement} codes
 *
 * @extends {Component<Refs>}
 */
class CstProductCodes extends Component {
  requiredRefs = ['skuLine', 'sku', 'mpnLine', 'mpn', 'codes'];

  /** @type {Element | null} */
  #target = null;

  connectedCallback() {
    super.connectedCallback();
    this.#target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    this.#target?.addEventListener(StandardEvents.productSelect, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#target?.removeEventListener(StandardEvents.productSelect, this.#onVariantUpdate);
  }

  /** @param {any} event - ProductSelectEvent; `event.promise` resolves with the selected variant. */
  #onVariantUpdate = (event) => {
    Promise.resolve(event.promise)
      .then((/** @type {any} */ result) => {
        const detail = result?.detail;
        if (!detail?.resource) return;
        if (detail.productId && String(detail.productId) !== this.dataset.productId) return;

        /** @type {Record<string, { sku?: string | null, mpn?: string | null }>} */
        const codes = JSON.parse(this.refs.codes.textContent || '{}');
        const entry = codes[String(detail.resource.id)];
        if (!entry) return;

        this.#set(this.refs.skuLine, this.refs.sku, entry.sku);
        this.#set(this.refs.mpnLine, this.refs.mpn, entry.mpn);
      })
      .catch((/** @type {any} */ error) => {
        if (error?.name !== 'AbortError') console.warn('[cst-product-codes]', error);
      });
  };

  /**
   * @param {HTMLElement} line
   * @param {HTMLElement} value
   * @param {string | null | undefined} text
   */
  #set(line, value, text) {
    value.textContent = text || '';
    line.hidden = !text;
  }
}

if (!customElements.get('cst-product-codes')) {
  customElements.define('cst-product-codes', CstProductCodes);
}
